import React, { useEffect, useState } from 'react';
import { Alert, Anchor, Stack, Text } from '@mantine/core';
import { LivePreSiteSourcesSchema, type LivePreSiteSources } from '@tinypro-audit/contracts';
import { fetchJson } from '../api/client';
import { reviewDate } from './ProjectReviewSupport';

export function ProjectJobAssociations({
  projectId,
}: {
  projectId: string;
}) {
  const [state, setState] = useState<LivePreSiteSources | null>(null);
  const [lastAvailable, setLastAvailable] = useState<Extract<LivePreSiteSources, { status: 'available' }> | null>(null);
  useEffect(() => {
    let stopped = false, busy = false;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    setState(null);
    setLastAvailable(null);
    const read = async () => {
      if (stopped || busy) return;
      busy = true;
      try {
        if (!document.hidden) {
          const next = await fetchJson(
            `/api/projects/${encodeURIComponent(projectId)}/pre-site-sources`,
            { signal: AbortSignal.any([controller.signal, AbortSignal.timeout(5000)]) },
            LivePreSiteSourcesSchema,
          );
          if (!stopped) {
            setState(next);
            if (next.status === 'available') setLastAvailable(next);
          }
        }
      } catch {
        if (!stopped) setState({ status: 'unavailable', checked_at: new Date().toISOString() });
      } finally {
        busy = false;
        if (!stopped) timer = setTimeout(() => void read(), 30_000);
      }
    };
    const onVisible = () => {
      if (!document.hidden) { clearTimeout(timer); void read(); }
    };
    void read();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [projectId]);
  if (!state) return <Text size="sm" c="dimmed" mt="sm">Checking live pre-site source links…</Text>;
  const sources = lastAvailable?.sources;
  if (state.status === 'available' && !sources?.associations.length && !sources?.conflicts.length)
    return null;
  return (
    <Stack gap="sm" mt="sm">
      {state.status === 'unavailable' && (
        <Alert color="yellow" title="Live pre-site source check unavailable">
          Saved reports and evidence remain available. Current source relationships could not be checked; this does not mean an assessment is missing.
          {lastAvailable && <Text size="sm">Source links below may be stale. Last checked {reviewDate(lastAvailable.checked_at)}.</Text>}
        </Alert>
      )}
      {!!sources?.conflicts.length && (
        <Alert color="yellow" title="Pre-site source link needs clarification">
          {sources.conflicts.map((conflict) => (
            <Text size="sm" key={`${conflict.st_job_id}:${conflict.code}`}>
              Job {conflict.st_job_id}{' '}
              {conflict.code === 'SOURCE_PROJECT_CONFLICT'
                ? 'has a source review for'
                : 'is already linked to'}{' '}
              project {conflict.conflicting_project_id}. Its assessment will not
              be imported through this job link. Verify the project and job
              relationship.
            </Text>
          ))}
        </Alert>
      )}
      {!!sources?.associations.length && (
        <details>
          <summary>Source job links · {sources.associations.length}</summary>
          <Stack gap="xs" mt="sm">
            {sources.associations.map((link) => (
              <div key={link.association_fingerprint}>
                <Text size="sm" fw={500}>
                  Job {link.observation.st_job_id} ·{' '}
                  {link.status === 'verified'
                    ? 'Relationship observed'
                    : 'Unverified relationship'}
                </Text>
                <Text size="xs" c="dimmed">
                  Observed {reviewDate(link.observation.observed_at)} ·{' '}
                  <Anchor
                    size="xs"
                    href={link.observation.source_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Project source
                  </Anchor>
                  {link.observation.verification && (
                    <>
                      {' '}
                      ·{' '}
                      <Anchor
                        size="xs"
                        href={link.observation.verification.job_source_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Job source
                      </Anchor>
                    </>
                  )}
                </Text>
              </div>
            ))}
            <Text size="xs" c="dimmed">
              A source link establishes where a historical review belongs. Order
              coverage and the original grade remain separate.
            </Text>
          </Stack>
        </details>
      )}
    </Stack>
  );
}
